import { forwardRef, useImperativeHandle, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useAuth } from '../../AuthContext';
import { ALL_INVENTORY_ITEMS_QUERY } from '../../graphql/inventory';
import { ObjectTypeFormHandle, ObjectTypeFormProps } from './types';
import InventorySearchSelect, { InventoryOption } from './InventorySearchSelect';

// Custom Cornice — per Quoting_markdown/cornices.md. Submitted through
// dedicated addCorniceDraftQuoteObject/updateCorniceDraftQuoteObject
// mutations (see DraftQuoteDetail.tsx's DEDICATED_TYPE_CONFIG/handleAddObject),
// same deviation Bench/Bed/Box Spring Cover/etc. already established.
//
// Width entered here is the WINDOW width — the cornice's own width is
// derived (OM = window + 2", IM = window − ¼"), same way Traversing Drape
// derives Finished Width, so the shop can keep measuring the window the way
// they already do. Returns are derived too (3.5" standard, 5.5" over another
// product, none for IM) and never stored.

interface Placements { top: boolean; bottom: boolean; sides: boolean; face: boolean }

const PLACEMENTS: [keyof Placements, string][] = [
  ['top', 'Top'], ['bottom', 'Bottom'], ['sides', 'Sides'], ['face', 'Face'],
];

function PlacementRow({ label, hint, value, onChange, disabled }: {
  label: string;
  hint?: string;
  value: Placements;
  onChange: (next: Placements) => void;
  disabled: boolean;
}) {
  return (
    <div className="add-item-form-row">
      <div className="form-group" style={{ width: 130 }}>
        <label>{label}{hint && <span style={{ color: '#9ca3af', fontWeight: 400 }}> {hint}</span>}</label>
      </div>
      {PLACEMENTS.map(([key, text]) => (
        <div className="form-group" key={key} style={{ width: 90 }}>
          <label>
            <input type="checkbox" checked={value[key]}
              onChange={(e) => onChange({ ...value, [key]: e.target.checked })} disabled={disabled} />
            {' '}{text}
          </label>
        </div>
      ))}
    </div>
  );
}

const CorniceForm = forwardRef<ObjectTypeFormHandle, ObjectTypeFormProps>((props, ref) => {
  const { user } = useAuth();
  const iv = props.initialValues;

  // Standard cornice is outside mount — default to it for a new object.
  const [mountType, setMountType] = useState<'outside' | 'inside'>((iv?.mountType as any) || 'outside');
  const [corniceShape, setCorniceShape] = useState<'straight' | 'shaped' | ''>((iv?.corniceShape as any) || '');
  const [overProduct, setOverProduct] = useState<boolean>(iv?.corniceOverProduct || false);
  const [windowWidth, setWindowWidth] = useState(iv?.widthInches != null ? String(iv.widthInches) : '');
  const [height, setHeight] = useState(iv?.heightInches != null ? String(iv.heightInches) : '');
  const [fabricDescription, setFabricDescription] = useState<string>(iv?.fabricDescription || '');
  const [fabricWidth, setFabricWidth] = useState(iv?.fabricWidthInches != null ? String(iv.fabricWidthInches) : '54');
  const [patternRepeat, setPatternRepeat] = useState(iv?.patternRepeatInches != null ? String(iv.patternRepeatInches) : '');
  const [trim, setTrim] = useState<Placements>({
    top: iv?.trimTop || false, bottom: iv?.trimBottom || false, sides: iv?.trimSides || false, face: iv?.trimFace || false,
  });
  const [trimDescription, setTrimDescription] = useState<string>(iv?.trimDescription || '');
  const [welt, setWelt] = useState<Placements>({
    top: iv?.weltTop || false, bottom: iv?.weltBottom || false, sides: iv?.weltSides || false, face: iv?.weltFace || false,
  });
  const [nailheads, setNailheads] = useState<Placements>({
    top: iv?.nailheadTop || false, bottom: iv?.nailheadBottom || false, sides: iv?.nailheadSides || false, face: iv?.nailheadFace || false,
  });
  const [nailheadColor, setNailheadColor] = useState<string>(iv?.nailheadColor || '');
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery(ALL_INVENTORY_ITEMS_QUERY, {
    variables: { domainId: user?.domain_id },
    skip: !user?.domain_id,
  });
  const fabricItems: InventoryOption[] = (data?.allInventoryItems?.nodes || [])
    .filter((i: any) => !i.archivedAt && i.inventoryType === 'FABRIC')
    .map((i: any) => ({ id: i.id, name: i.name, costCents: i.costCents ?? 0, widthInches: i.widthInches ?? null, inventoryType: i.inventoryType }));

  const isInside = mountType === 'inside';
  const anyTrim = trim.top || trim.bottom || trim.sides || trim.face;
  const anyNailhead = nailheads.top || nailheads.bottom || nailheads.sides || nailheads.face;

  // Live readout of the derived dimensions (same arithmetic as
  // corniceDimensions() on the server).
  const windowWidthNum = parseFloat(windowWidth);
  const derived = !isNaN(windowWidthNum)
    ? {
        width: isInside ? windowWidthNum - 0.25 : windowWidthNum + 2,
        returns: isInside ? 0 : (overProduct ? 5.5 : 3.5),
      }
    : null;

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!corniceShape) { setError('Shape is required'); return null; }
      if (!windowWidth) { setError('Window width is required'); return null; }
      if (!height) { setError('Height is required'); return null; }
      setError(null);
      return {
        mountType,
        widthInches: parseFloat(windowWidth),
        heightInches: parseFloat(height),
        corniceShape,
        corniceOverProduct: isInside ? false : overProduct,
        fabricDescription: fabricDescription.trim() || null,
        fabricWidthInches: fabricWidth ? parseFloat(fabricWidth) : null,
        patternRepeatInches: patternRepeat ? parseFloat(patternRepeat) : null,
        trimTop: trim.top,
        trimBottom: trim.bottom,
        trimSides: trim.sides,
        trimFace: trim.face,
        trimDescription: anyTrim ? (trimDescription.trim() || null) : null,
        weltTop: welt.top,
        weltBottom: welt.bottom,
        weltSides: welt.sides,
        weltFace: welt.face,
        nailheadTop: nailheads.top,
        nailheadBottom: nailheads.bottom,
        nailheadSides: nailheads.sides,
        nailheadFace: nailheads.face,
        nailheadColor: anyNailhead ? (nailheadColor.trim() || null) : null,
      };
    },
  }));

  return (
    <div>
      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Mount Type</label>
          <select className="input" value={mountType}
            onChange={(e) => setMountType(e.target.value as 'outside' | 'inside')} disabled={props.disabled}>
            <option value="outside">Outside Mount (standard)</option>
            <option value="inside">Inside Mount (rare)</option>
          </select>
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Shape</label>
          <select className="input" value={corniceShape}
            onChange={(e) => setCorniceShape(e.target.value as 'straight' | 'shaped' | '')} disabled={props.disabled}>
            <option value="">Select...</option>
            <option value="straight">Straight</option>
            <option value="shaped">Shaped bottom (+$10/ft)</option>
          </select>
        </div>
        {!isInside && (
          <div className="form-group" style={{ flex: 1 }}>
            <label>
              <input type="checkbox" checked={overProduct}
                onChange={(e) => setOverProduct(e.target.checked)} disabled={props.disabled} />
              {' '}Goes over another product (5.5" returns)
            </label>
          </div>
        )}
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Window Width (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={windowWidth}
            onChange={(e) => setWindowWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Cornice Height (inches)</label>
          <input type="number" min="0" step="0.25" className="input" value={height}
            onChange={(e) => setHeight(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label>Cornice Width / Returns <span style={{ color: '#9ca3af', fontWeight: 400 }}>(calculated)</span></label>
          <div style={{ padding: '8px 0', fontSize: 14, color: '#374151' }}>
            {derived
              ? `${derived.width}" wide · ${derived.returns ? `${derived.returns}" returns` : 'no returns'}`
              : '—'}
          </div>
        </div>
      </div>

      <div className="add-item-form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label>Fabric Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <InventorySearchSelect
            items={fabricItems}
            value={fabricDescription}
            onChange={(name) => setFabricDescription(name)}
            placeholder="Search inventory or type fabric description..."
            disabled={props.disabled}
          />
        </div>
        <div className="form-group" style={{ width: 120 }}>
          <label>Fabric Width (in)</label>
          <input type="number" min="0" step="1" className="input" value={fabricWidth}
            onChange={(e) => setFabricWidth(e.target.value)} disabled={props.disabled} />
        </div>
        <div className="form-group" style={{ width: 140 }}>
          <label>Pattern Repeat (in) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
          <input type="number" min="0" step="0.25" className="input" value={patternRepeat}
            onChange={(e) => setPatternRepeat(e.target.value)} disabled={props.disabled} />
        </div>
      </div>

      <PlacementRow label="Trim" value={trim} onChange={setTrim} disabled={props.disabled} />
      {anyTrim && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Trim Description <span style={{ color: '#9ca3af', fontWeight: 400 }}>(shared by every trim placement)</span></label>
            <input type="text" className="input" value={trimDescription}
              onChange={(e) => setTrimDescription(e.target.value)} disabled={props.disabled} />
          </div>
        </div>
      )}

      <PlacementRow label="Self Welt" value={welt} onChange={setWelt} disabled={props.disabled} />

      <PlacementRow label="Nailheads" value={nailheads} onChange={setNailheads} disabled={props.disabled} />
      {anyNailhead && (
        <div className="add-item-form-row">
          <div className="form-group" style={{ flex: 1 }}>
            <label>Nailhead Color</label>
            <input type="text" className="input" value={nailheadColor}
              onChange={(e) => setNailheadColor(e.target.value)} disabled={props.disabled} />
          </div>
        </div>
      )}

      {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
    </div>
  );
});

export default CorniceForm;
